(() => {
  const parseJson = (el, fallback) => {
    if (!el) return fallback;
    try {
      return JSON.parse(el.textContent || '');
    } catch (_) {
      return fallback;
    }
  };

  const esc = (value) =>
    String(value == null ? '' : value)
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');

  const normalize = (value) =>
    String(value || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();

  const classifyHeading = (title) => {
    const n = normalize(title);
    if (n.includes('kort sammanfattning')) return 'summary';
    if (n.includes('egenskaper')) return 'benefits';
    if (n.includes('fordelar')) return 'benefits';
    if (n.includes('anvandningsomrade')) return 'use';
    if (n.includes('teknisk information') || n.includes('teknisk data')) return 'technical';
    if (n.includes('applicering')) return 'application';
    if (n.includes('forbrukning') || n.includes('torktid')) return 'consumption';
    if (n.includes('produktblad') || n.includes('sakerhetsdatablad') || n.includes('dokumentation')) return 'documents';
    if (n.includes('dokument') || n.includes('sakerhet')) return 'documents';
    if (n.includes('faq')) return 'faq';
    if (n.includes('kallsida')) return 'source';
    return 'other';
  };

  const renderSections = (rows) => {
    const clean = Array.isArray(rows) ? rows.filter((row) => row && (row.html || row.title)) : [];
    if (!clean.length) return '';
    return clean
      .map((row) => {
        const title = esc(row.title || '');
        const html = row.html || '';
        return `<section class="bp-tabs__section">${title ? `<p><strong>${title}</strong></p>` : ''}${html}</section>`;
      })
      .join('');
  };

  const renderDocs = (rows) => {
    const clean = Array.isArray(rows) ? rows.filter((row) => row && row.url) : [];
    if (!clean.length) return '';
    const items = clean
      .map(
        (row) =>
          `<li><a href="${esc(row.url || '#')}" target="_blank" rel="noopener noreferrer">${esc(
            row.title || 'Dokument'
          )}</a></li>`
      )
      .join('');
    return `<ul class="bp-tabs__docs">${items}</ul>`;
  };

  const getTab = (root, key) => root.querySelector(`[role="tab"][data-bp-tab="${key}"]`);
  const getPanel = (root, key) => root.querySelector(`[role="tabpanel"][id$="-${key}"]`);

  const hasRenderableContent = (panel) => {
    if (!panel) return false;
    const text = (panel.textContent || '').replace(/\s+/g, ' ').trim();
    const rich = panel.querySelector('img, table, ul, ol, a, section, p, div, figure, iframe');
    return text.length > 0 || !!rich;
  };

  const getHeadingFromNode = (node) => {
    if (!node || node.nodeType !== 1) return '';
    if (node.matches('h1, h2, h3, h4, h5, h6')) {
      return (node.textContent || '').trim();
    }
    if (node.matches('p')) {
      const strong = node.querySelector('strong');
      const pText = (node.textContent || '').trim();
      const strongText = (strong && strong.textContent ? strong.textContent : '').trim();
      if (strong && pText && strongText && pText === strongText) {
        return strongText;
      }
    }
    return '';
  };

  const splitDescriptionIntoSections = (html) => {
    const wrap = document.createElement('div');
    wrap.innerHTML = html || '';

    const sections = [];
    let current = { title: '', nodes: [] };

    const pushCurrent = () => {
      const temp = document.createElement('div');
      current.nodes.forEach((node) => temp.appendChild(node));
      const sectionHtml = temp.innerHTML.trim();
      const title = (current.title || '').trim();
      if (title || sectionHtml) {
        sections.push({ title, html: sectionHtml, kind: classifyHeading(title) });
      }
    };

    Array.from(wrap.childNodes).forEach((node) => {
      const heading = getHeadingFromNode(node);
      if (heading) {
        pushCurrent();
        current = { title: heading, nodes: [] };
        return;
      }
      current.nodes.push(node.cloneNode(true));
    });

    pushCurrent();
    return sections.filter((s) => s.title || s.html);
  };

  const KIILTO_DEFAULT_LAYOUT = {
    desc: ['summary'],
    technical: ['benefits', 'use', 'technical'],
    instructions: ['application', 'consumption'],
    documents: ['documents'],
  };

  const KIILTO_LAYOUT_BY_HANDLE = {
    'kiilto-pro-plan-express': {
      desc: ['summary'],
      technical: ['benefits', 'use'],
      instructions: ['application', 'consumption'],
      documents: ['documents'],
    },
    'kiilto-pro-plan-craft': {
      desc: ['summary'],
      technical: ['benefits', 'use'],
      instructions: ['application', 'consumption'],
      documents: ['documents'],
    },
    'kiilto-pro-plan-industry': {
      desc: ['summary'],
      technical: ['benefits', 'use', 'technical'],
      instructions: ['application', 'consumption'],
      documents: ['documents'],
    },
    'kiilto-pro-plan-vario': {
      desc: ['summary'],
      technical: ['benefits', 'use', 'technical'],
      instructions: ['application', 'consumption'],
      documents: ['documents'],
    },
    'kiilto-pro-fine-combi': {
      desc: ['summary'],
      technical: ['benefits', 'use'],
      instructions: ['application', 'consumption'],
      documents: ['documents'],
    },
  };

  const pickSections = (sections, kinds) => {
    const allow = new Set(Array.isArray(kinds) ? kinds : []);
    if (!allow.size) return [];
    return sections.filter((section) => allow.has(section.kind));
  };

  const firstNonEmpty = (...lists) => {
    for (const list of lists) {
      if (Array.isArray(list) && list.length) return list;
    }
    return [];
  };

  const buildKiiltoAutoPayload = (root) => {
    const vendor = (root.dataset.bpVendor || '').toLowerCase();
    if (!vendor.includes('kiilto')) return null;
    const handle = (root.dataset.bpHandle || '').toLowerCase();
    const layout = KIILTO_LAYOUT_BY_HANDLE[handle] || KIILTO_DEFAULT_LAYOUT;

    const descPanel = getPanel(root, 'desc');
    if (!descPanel) return null;

    const sections = splitDescriptionIntoSections(descPanel.innerHTML || '');
    if (!sections.length) return null;

    const summarySections = firstNonEmpty(
      pickSections(sections, layout.desc),
      sections.filter((s) => s.kind !== 'documents')
    ).slice(0, 1);
    if (summarySections.length) {
      descPanel.innerHTML = renderSections(summarySections);
    }

    const technicalSections = firstNonEmpty(
      pickSections(sections, layout.technical),
      sections.filter((s) => ['benefits', 'use', 'technical'].includes(s.kind))
    );
    const instructionsSections = firstNonEmpty(
      pickSections(sections, layout.instructions),
      sections.filter((s) => ['application', 'consumption'].includes(s.kind))
    );

    const docSection = firstNonEmpty(
      pickSections(sections, layout.documents),
      sections.filter((s) => s.kind === 'documents')
    )[0];
    let documents = [];
    if (docSection) {
      const docWrap = document.createElement('div');
      docWrap.innerHTML = docSection.html || '';
      documents = Array.from(docWrap.querySelectorAll('a[href]'))
        .map((a) => ({
          title: (a.textContent || 'Dokument').trim() || 'Dokument',
          url: (a.getAttribute('href') || '').trim(),
        }))
        .filter((row) => row.url);
    }

    return {
      technical_sections: technicalSections,
      instructions_sections: instructionsSections,
      documents,
    };
  };

  const renderSpecsTable = (specs) => {
    if (!specs || typeof specs !== 'object') return '';
    const rows = Object.entries(specs)
      .filter(([key, value]) => String(key || '').trim() && String(value || '').trim())
      .map(([key, value]) => `<tr><th>${esc(key)}</th><td>${esc(value)}</td></tr>`)
      .join('');
    return rows ? `<table class="bp-tabs__specs"><tbody>${rows}</tbody></table>` : '';
  };

  const variantMapCache = new Map();

  const getVariantInput = (root) => {
    const scope = root.closest('section, .shopify-section, [data-section-id]') || document;
    return (
      scope.querySelector('form[action*="/cart/add"] [name="id"]') ||
      scope.querySelector('input[name="id"]') ||
      null
    );
  };

  const getSelectedVariantId = (root) => {
    const input = getVariantInput(root);
    if (input && String(input.value || '').trim()) return String(input.value).trim();
    try {
      const fromUrl = new URL(window.location.href).searchParams.get('variant');
      return fromUrl ? String(fromUrl).trim() : '';
    } catch (_) {
      return '';
    }
  };

  const loadVariantSkuMap = async (handle) => {
    const key = String(handle || '').trim();
    if (!key) return {};
    if (variantMapCache.has(key)) return variantMapCache.get(key);

    const map = {};
    try {
      const response = await fetch(`/products/${encodeURIComponent(key)}.js`, {
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) {
        variantMapCache.set(key, map);
        return map;
      }
      const productJson = await response.json();
      const variants = Array.isArray(productJson && productJson.variants) ? productJson.variants : [];
      variants.forEach((row) => {
        const id = row && row.id != null ? String(row.id) : '';
        const sku = row && row.sku != null ? String(row.sku).trim() : '';
        if (id && sku) map[id] = sku;
      });
    } catch (_) {
      // Keep empty map on fetch failure; fallback content remains visible.
    }
    variantMapCache.set(key, map);
    return map;
  };

  const setupVariantSpecificTechnicalPanel = async (root, panel, payload) => {
    if (!root || !panel || !payload || typeof payload !== 'object') return;
    const variantSpecsBySku =
      payload.variant_specs_by_sku && typeof payload.variant_specs_by_sku === 'object'
        ? payload.variant_specs_by_sku
        : null;
    if (!variantSpecsBySku) return;

    const skus = Object.keys(variantSpecsBySku);
    if (!skus.length) return;

    const defaultHtml = panel.innerHTML || renderSpecsTable(variantSpecsBySku[skus[0]]) || '';
    if (!panel.innerHTML && defaultHtml) panel.innerHTML = defaultHtml;

    const handle = (root.dataset.bpHandle || '').trim();
    const idToSku = await loadVariantSkuMap(handle);
    let lastRenderKey = '';

    const renderForCurrentVariant = () => {
      if (!document.contains(root) || !document.contains(panel)) return;
      const variantId = getSelectedVariantId(root);
      const sku = variantId ? idToSku[String(variantId)] : '';
      const variantSpecs = sku ? variantSpecsBySku[sku] : null;
      const nextHtml = renderSpecsTable(variantSpecs) || defaultHtml;
      const nextKey = `${variantId || 'none'}::${sku || 'none'}::${nextHtml.length}`;
      if (nextKey === lastRenderKey) return;
      lastRenderKey = nextKey;
      panel.innerHTML = nextHtml;
    };

    renderForCurrentVariant();

    const onChange = () => renderForCurrentVariant();
    document.addEventListener('change', onChange, true);

    const timer = window.setInterval(() => {
      if (!document.contains(root)) {
        window.clearInterval(timer);
        document.removeEventListener('change', onChange, true);
        return;
      }
      renderForCurrentVariant();
    }, 400);
  };

  const initTabs = (root) => {
    if (!root || root.dataset.bpTabsReady === '1') return;
    root.dataset.bpTabsReady = '1';

    const allTabs = () => Array.from(root.querySelectorAll('[role="tab"]'));
    const visibleTabs = () => allTabs().filter((tab) => !tab.hidden);

    const activate = (tab) => {
      const tabs = visibleTabs();
      if (!tabs.length) return;
      const target = tab && !tab.hidden ? tab : tabs[0];

      allTabs().forEach((candidate) => {
        const selected = candidate === target;
        candidate.setAttribute('aria-selected', selected ? 'true' : 'false');
        candidate.tabIndex = selected ? 0 : -1;
        const panel = root.querySelector('#' + candidate.getAttribute('aria-controls'));
        if (panel) panel.hidden = !selected;
      });
    };

    root.addEventListener('click', (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (!tab || tab.hidden || !root.contains(tab)) return;
      activate(tab);
    });

    root.addEventListener('keydown', (event) => {
      const tab = event.target.closest('[role="tab"]');
      if (!tab || tab.hidden) return;
      if (!['ArrowRight', 'ArrowLeft', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();

      const tabs = visibleTabs();
      if (!tabs.length) return;

      const index = tabs.indexOf(tab);
      let next = index;
      if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;
      if (event.key === 'ArrowLeft') next = (index - 1 + tabs.length) % tabs.length;
      if (event.key === 'Home') next = 0;
      if (event.key === 'End') next = tabs.length - 1;

      tabs[next].focus();
      activate(tabs[next]);
    });

    const payloadEl = root.querySelector('[data-bp-tabs-payload]');
    const payload = parseJson(payloadEl, null);

    const technicalPanel = getPanel(root, 'technical');
    const instructionsPanel = getPanel(root, 'instructions');
    const documentsPanel = getPanel(root, 'documents');

    let resolvedPayload = payload && typeof payload === 'object' ? payload : null;

    if (!resolvedPayload) {
      const auto = buildKiiltoAutoPayload(root);
      if (auto) resolvedPayload = auto;
    }

    if (resolvedPayload) {
      if (technicalPanel) technicalPanel.innerHTML = renderSections(resolvedPayload.technical_sections);
      if (instructionsPanel) instructionsPanel.innerHTML = renderSections(resolvedPayload.instructions_sections);
      if (documentsPanel) documentsPanel.innerHTML = renderDocs(resolvedPayload.documents);

      if (technicalPanel && !hasRenderableContent(technicalPanel)) {
        const variantSpecsBySku =
          resolvedPayload.variant_specs_by_sku && typeof resolvedPayload.variant_specs_by_sku === 'object'
            ? resolvedPayload.variant_specs_by_sku
            : null;
        if (variantSpecsBySku) {
          const firstSpecs = variantSpecsBySku[Object.keys(variantSpecsBySku)[0]];
          technicalPanel.innerHTML = renderSpecsTable(firstSpecs);
        }
      }

      if (technicalPanel) {
        setupVariantSpecificTechnicalPanel(root, technicalPanel, resolvedPayload);
      }
    } else {
      const legacyAssets = parseJson(root.querySelector('[data-bp-legacy-dansani-assets]'), []);
      const legacySpecs = parseJson(root.querySelector('[data-bp-legacy-dansani-specs]'), {});

      if (technicalPanel && legacySpecs && typeof legacySpecs === 'object') {
        const rows = Object.entries(legacySpecs)
          .map(([key, value]) => `<tr><th>${esc(key)}</th><td>${esc(value)}</td></tr>`)
          .join('');
        technicalPanel.innerHTML = rows ? `<table class="bp-tabs__specs"><tbody>${rows}</tbody></table>` : '';
      }

      if (documentsPanel) {
        documentsPanel.innerHTML = renderDocs(legacyAssets);
      }

      if (instructionsPanel) {
        instructionsPanel.innerHTML = '';
      }
    }

    const tabKeys = ['desc', 'technical', 'instructions', 'documents'];
    tabKeys.forEach((key) => {
      if (key === 'desc') return;
      const tab = getTab(root, key);
      const panel = getPanel(root, key);
      const show = hasRenderableContent(panel);
      if (tab) tab.hidden = !show;
      if (panel && !show) {
        panel.hidden = true;
        panel.innerHTML = '';
      }
    });

    const tabList = root.querySelector('[role="tablist"]');
    const visible = visibleTabs();
    if (tabList) tabList.hidden = visible.length <= 1;

    const selected = root.querySelector('[role="tab"][aria-selected="true"]:not([hidden])') || visible[0];
    if (selected) activate(selected);
  };

  const initAll = (scope) => {
    const base = scope && scope.querySelectorAll ? scope : document;
    base.querySelectorAll('[data-bp-tabs]').forEach(initTabs);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target || document);
  });
})();
